import React, { useState, useContext, useEffect } from 'react';
import { AggregationQueryContext } from 'src/components/CategoryListingPage/AggregationQueryProvider';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { QuickQuote } from 'src/elements/QuickQuote/QuickQuote';
import { ProductListItem } from 'src/components/CategoryListingPage/ProductListItem';
import { ColorSwatchesProvider } from 'src/components/CategoryListingPage/ColorSwatchesProvider';
import { skip, tap } from 'rxjs';
import { AggregationSelections, selectionToValues } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { BehaviorSubject } from 'rxjs';
import { AggregationDisplay } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import classnames from 'classnames';

import { ProductListItem as ProductListItemType } from './utils/productListItem';
import classes from './ProductList.scss';

export type ProductListItemsProps = {
    products: (string | ProductListItemType)[];
    loading: boolean;
    gtmSelectItem(index: number): void;
    listId?: string;
    onChange?: (checked: boolean, code: string, label: string) => void;
    selection$?: BehaviorSubject<AggregationSelections>;
    algolia?: boolean;
    algoliaSelections?: string[];
    modalMode?: boolean;
    onAction?: (id: number, name: string, options?: any, image?: string) => void;
};

const mapSelectionsColors = (selectionsData) => selectionsData.map((selectionsItem) => selectionsItem.option.label);

export const ProductListItems: React.FC<ProductListItemsProps> = ({
    loading,
    gtmSelectItem,
    products,
    listId,
    onChange,
    selection$,
    algolia,
    algoliaSelections,
    modalMode = false,
    onAction,
}) => {
    const { vat } = useVatCausesRender();

    const { mappedAggregations, tagsAggregation } = useContext(AggregationQueryContext);
    const [selections, setValues] = useState<AggregationDisplay[] | undefined>(
        selection$ ? selectionToValues(mappedAggregations, selection$.value) : undefined,
    );

    const currentColorSelections = algoliaSelections || mapSelectionsColors(selections);

    useEffect(() => {
        const sub = selection$
            ?.pipe(
                skip(1), // because it's a BehaviorSubject, it will project the first element
                tap((selections) => {
                    setValues(selectionToValues(mappedAggregations, selections as any));
                }),
            )
            .subscribe();
        return () => sub?.unsubscribe();
    }, [mappedAggregations, selection$]);

    const tagsSelected = selections?.map((el) => el.attribute_code === 'product_tags' && el.option.label);

    const multipackSelected =
        selections?.filter((el) => el.attribute_code === 'multipack').map((el) => el.option.value) || [];

    const productIds = products.flatMap((product) => (typeof product === 'string' ? [] : [product.id]));

    return (
        <ColorSwatchesProvider ids={productIds}>
            <ul
                className={classnames(classes.productList, {
                    [classes.modalMode]: modalMode,
                })}
            >
                {products.map((product, index) => {
                    // QuickQuote for mobile and tablet screen
                    if (product === 'quickQuoteWrapper1' || product === 'quickQuoteWrapper2') {
                        return (
                            <div className={classes[product]} key={product}>
                                <div className="contextx">
                                    {' '}
                                    <QuickQuote sku={listId || ''} isPcp={true} />
                                </div>
                            </div>
                        );
                    }
                    if (typeof product !== 'string') {
                        return (
                            <ProductListItem
                                key={product.id}
                                product={product}
                                loading={loading}
                                gtmSelectItem={gtmSelectItem}
                                index={index}
                                vat={vat}
                                onChange={onChange}
                                tagsSelected={tagsSelected}
                                tagsAggregation={tagsAggregation}
                                algolia={algolia}
                                colorSelections={currentColorSelections}
                                onAction={onAction}
                                multipackSelected={multipackSelected}
                            />
                        );
                    }
                })}
            </ul>
        </ColorSwatchesProvider>
    );
};
